import InfoIcon from '@mui/icons-material/Info';
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Grid,
  Typography,
} from '@mui/material';
import { getDataHubLightReport } from 'lib/api/datahub/reports';
import { getIntegrationStatusByInternalName } from 'lib/api/integrations';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import DialogTitle from 'lib/designSystem/Title/DialogTitle';
import { errorHandler } from 'lib/functions/errors';
import React, { useEffect } from 'react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

const EXPORT_OPTIONS = {
  'Ventas / Compras': 'invoice',
  Factoring: 'factoring',
  'Carpeta Tributaria': 'iva',
  TGR: 'tgr',
  'Mercado Público': 'publicMarket',
  Deuda: 'debt',
  'Buro de Crédito': 'buro',
  'Mora Previsional': 'socialSecurityArrear',
  'Multas Laborales': 'laborLawFines',
};

type Props = {
  id?: number;
  open: boolean;
  onClose: () => void;
};

const DatahubReport: React.FC<Props> = ({ id, open, onClose }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [integrationsLoading, setIntegrationsLoading] = useState(false);
  const [hasSuperDebt, setHasSuperDebt] = useState(false);
  const [hasBuro, setHasBuro] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<Set<string>>(
    new Set(),
  );

  const fetchIntegrations = async () => {
    try {
      setIntegrationsLoading(true);
      const [superDebtIntegration, buroIntegration] = await Promise.all([
        getIntegrationStatusByInternalName(dispatch, 'super-debt'),
        getIntegrationStatusByInternalName(dispatch, 'buro'),
      ]);
      const superDebtStatus = superDebtIntegration?.status || false;
      const buroStatus = buroIntegration?.status || false;
      setHasSuperDebt(superDebtStatus);
      setHasBuro(buroStatus);

      const availableOptions = Object.values(EXPORT_OPTIONS).filter((value) => {
        if (value === 'buro' && !buroStatus) return false;
        if (value === 'debt' && !superDebtStatus) return false;
        return true;
      });
      setSelectedOptions(new Set(availableOptions));
    } catch (error) {
      const availableOptions = Object.values(EXPORT_OPTIONS).filter((value) => {
        if (value === 'buro' && !hasBuro) return false;
        if (value === 'debt' && !hasSuperDebt) return false;
        return true;
      });
      setSelectedOptions(new Set(availableOptions));
    } finally {
      setIntegrationsLoading(false);
    }
  };

  const handleToggleOption = (optionValue: string) => {
    setSelectedOptions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(optionValue)) {
        newSet.delete(optionValue);
      } else {
        newSet.add(optionValue);
      }
      return newSet;
    });
  };

  const exportLightReport = async (id: number | undefined) => {
    try {
      if (!id) return;
      setLoading(true);
      const url = new URLSearchParams();
      Array.from(selectedOptions).forEach((option) => {
        url.append('sections', option);
      });

      const query = url.toString();
      const file = await getDataHubLightReport(dispatch, id, query);
      if (!file) {
        alert('No se pudo generar el reporte');
        return;
      }
      const fileUrl = URL.createObjectURL(file);
      window.open(fileUrl, '_blank');
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchIntegrations();
  }, []);

  if (integrationsLoading) return <CenteredCircularProgress type="layout" />;

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle
        title="Reporte de Datahub"
        onClose={onClose}
        icon={<InfoIcon color="primary" />}
        color="primary"
      />
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Selecciona qué secciones deseas incluir en el reporte:
        </Typography>
        <Grid container spacing={2}>
          {Object.entries(EXPORT_OPTIONS)
            .filter(([_label, value]) => {
              if (value === 'buro' && !hasBuro) return false;
              if (value === 'debt' && !hasSuperDebt) return false;
              return true;
            })
            .map(([label, value]) => (
              <Grid item xs={12} sm={6} key={value}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedOptions.has(value)}
                      onChange={() => handleToggleOption(value)}
                      color="primary"
                      size="small"
                    />
                  }
                  label={label}
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: '0.875rem',
                    },
                  }}
                />
              </Grid>
            ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <CustomButton onClick={onClose} color="secondary">
          Cancelar
        </CustomButton>
        <CustomButton
          onClick={() => exportLightReport(id)}
          disabled={selectedOptions.size === 0 || loading}
          loading={loading}
        >
          Exportar Reporte
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default DatahubReport;
